import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiUpload, FiImage, FiTrash2, FiSave } from 'react-icons/fi';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const LogoManager = () => {
  const [currentLogo, setCurrentLogo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const docRef = doc(db, 'shop', 'logo');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists() && docSnap.data().url) {
        const logoUrl = docSnap.data().url;
        setCurrentLogo(logoUrl);
        setPreview(logoUrl);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    } finally {
      setLoading(false);
    }
  };

  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          
          // Keep original size but compress quality
          let width = img.width;
          let height = img.height;
          
          // If too large, resize
          const maxSize = 800;
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height * maxSize) / width;
              width = maxSize;
            } else {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          
          // Compress to keep under 900KB
          let quality = 0.9;
          let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          
          while (compressedDataUrl.length > 900000 && quality > 0.5) {
            quality -= 0.1;
            compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(compressedDataUrl);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        setPreview(compressed);
        setHasChanges(true);
      } catch (error) {
        console.error('Error processing image:', error);
        alert('Failed to process image. Please try a smaller image.');
      }
    }
  };

  const handleSave = async () => {
    if (!preview) {
      alert('Please select a logo first');
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'shop', 'logo'), { 
        url: preview,
        updatedAt: new Date().toISOString()
      });
      setCurrentLogo(preview);
      setHasChanges(false);
      alert('Logo saved successfully!');
    } catch (error) {
      console.error('Error saving logo:', error);
      alert('Failed to save logo. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (window.confirm('Are you sure you want to remove the logo?')) {
      setSaving(true);
      try {
        await setDoc(doc(db, 'shop', 'logo'), { url: null });
        setCurrentLogo(null);
        setPreview(null);
        setHasChanges(false);
        alert('Logo removed successfully!');
      } catch (error) {
        console.error('Error removing logo:', error);
        alert('Failed to remove logo. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleCancel = () => {
    setPreview(currentLogo);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white">Loading logo...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <FiImage className="text-2xl sm:text-3xl text-white" />
        <h2 className="text-xl sm:text-2xl font-bold text-white">Shop Logo</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Upload Section */}
        <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Upload New Logo</h3>
          
          <label className="block">
            <div className="border-2 border-dashed border-white/20 rounded-xl p-6 sm:p-8 text-center hover:border-white/40 transition-all cursor-pointer bg-white/5">
              <FiUpload className="mx-auto text-3xl sm:text-4xl text-white/60 mb-2 sm:mb-3" />
              <p className="text-white/80 mb-1 sm:mb-2 text-sm sm:text-base">Click to upload logo</p>
              <p className="text-white/40 text-xs sm:text-sm">PNG, JPG, SVG recommended</p>
              <p className="text-white/40 text-xs mt-1">Image will be compressed if needed</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </label>

          {preview && (
            <div className="mt-4">
              <div className="bg-white/10 rounded-lg p-4">
                <img
                  src={preview}
                  alt="Logo Preview"
                  className="w-full h-32 sm:h-48 object-contain"
                />
              </div>
              {hasChanges && (
                <p className="text-yellow-300 text-xs mt-2 text-center">
                  ⚠️ Unsaved changes - click "Save Logo" below
                </p>
              )}
            </div>
          )}
        </div>

        {/* Current Logo */}
        <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Current Logo</h3>
          
          {currentLogo ? (
            <div className="space-y-4">
              <div className="bg-white/10 rounded-lg p-4 flex items-center justify-center h-32 sm:h-48">
                <img
                  src={currentLogo}
                  alt="Current Logo"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <button
                onClick={handleRemove}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all text-sm sm:text-base disabled:opacity-50"
              >
                <FiTrash2 />
                <span>{saving ? 'Removing...' : 'Remove Logo'}</span>
              </button>
            </div>
          ) : (
            <div className="h-32 sm:h-48 flex items-center justify-center text-white/40 text-sm sm:text-base bg-white/5 rounded-lg">
              No logo uploaded yet
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="mt-4 sm:mt-6 flex gap-3 justify-end">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-all text-sm sm:text-base disabled:opacity-50"
          >
            Cancel
          </button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-gray-100 transition-all text-sm sm:text-base disabled:opacity-50 shadow-lg"
          >
            <FiSave />
            <span>{saving ? 'Saving...' : 'Save Logo'}</span>
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default LogoManager;